import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { defaultMeta } from '@/constants/defaultMeta';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { StorePaymentLogsData } from '@/services/payment/payment.types';
import { getColumns } from './Column';

export function StorePaymentLogsTable({
  data,
  isLoading = false,
  pagination = defaultMeta,
}: {
  data: StorePaymentLogsData[];
  isLoading?: boolean;
  pagination?: Pagination;
}) {
  const { t, locale } = useDynamicLocale();
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { data: locationData } = useAttributes('location');
  const locationOptions = React.useMemo(() => {
    return (
      locationData?.map((item) => ({
        label: locale === 'en' ? item.labelEng : item.label,
        value: item.value,
      })) ?? []
    );
  }, [locationData, locale]);

  const columns = React.useMemo(() => getColumns(t, locationOptions, locale), [t, locationOptions, locale]);

  const { table, locationFilter, onLocationFilterChange, clubFilter, onClubFilterChange } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      title: <Translation id="paymentManagement.storePaymentLogs.table.title" />,
      loading: isLoading,
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
    },
    getRowId: (originalRow, index) => `${originalRow.clubId || originalRow.id}-${originalRow.sn ?? index + 1}`,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar
          locationFilter={locationFilter as string}
          onLocationFilterChange={onLocationFilterChange}
          clubFilter={clubFilter as string}
          onClubFilterChange={onClubFilterChange}
        />
      </DataTable>
    </>
  );
}

export const StoresLogTable = StorePaymentLogsTable;
